import { Address } from "../Objects/models/Address";
import { AddressData, AddressErrors, VoieOption } from "../Hooks/InterfaceAiciOnboarding";
import validateZipCode from "../Validation/zipCode";
import { lettreVoieList, typeVoie } from "./Function";

/**
 * Adresse « au format du formulaire AICI » : numéro (obligatoire), lettre,
 * type de voie, libellé, complément, lieu-dit, code postal, ville. Utilisé par
 * l'inscription AICI et par le profil du client.
 *
 * Convention de stockage (identique à l'inscription AICI) :
 * streetName = lettre + type + libellé, streetNameShort = libellé,
 * extraInformation = complément, formattedAddress = « 6 Bis Avenue Henri
 * Martin, 75016 Paris ».
 */

/** Libellé de voie / complément : lettres (accentuées), chiffres, espace, apostrophe, tiret. */
export const STREET_TEXT_PATTERN = /^[0-9a-zA-ZÀ-ÿ '’\-]+$/;
/** Complément lieu-dit (pattern fourni par l'URSSAF). */
export const LIEU_DIT_PATTERN =
    /^[0-9a-zA-ZáÁàÀâÂäÄéÉèÈëËêÊìÌíÍïÏîÎñÑòÒóÓôÔöÖùÙúÚüÜûÛçÇýÝ ÿŸŒœÆæ'\-\s]+( [0-9a-zA-ZáÁàÀâÂäÄéÉèÈëËêÊìÌíÍïÏîÎñÑòÒóÓôÔöÖùÙúÚüÜûÛçÇýÝ ÿŸŒœÆæ'\-\s]+)*$/;

export const STREET_LABEL_MAX = 28;
export const COMPLEMENT_MAX = 38;

/** Abréviations courantes rencontrées dans les adresses Google Places. */
const STREET_TYPE_ABBREVIATIONS: Record<string, string> = {
    av: "Avenue", ave: "Avenue",
    bd: "Boulevard", bld: "Boulevard", blvd: "Boulevard", boul: "Boulevard",
    r: "Rue",
    pl: "Place",
    ch: "Chemin", chem: "Chemin",
    imp: "Impasse",
    all: "Allée",
    sq: "Square",
    rte: "Route",
    fg: "Faubourg", fbg: "Faubourg",
    crs: "Cours",
    qu: "Quai",
    pge: "Passage",
    res: "Résidence",
    ham: "Hameau",
    prom: "Promenade",
    esp: "Esplanade",
    lot: "Lotissement",
};

export const emptyAddressData = (): AddressData => ({
    streetNumber: "",
    streetLetter: null,
    streetType: null,
    streetLabel: "",
    complement: "",
    lieuDit: "",
    zipCode: "",
    city: "",
});

/** Mêmes règles que le serveur (ManualAddressService) : le numéro est obligatoire. */
export const computeAddressErrors = (data: AddressData): AddressErrors => {
    const errs: AddressErrors = {};
    if (!/^\d{1,4}$/.test(data.streetNumber)) errs.streetNumber = "Numéro de voie obligatoire (4 chiffres maximum)";
    if (!data.streetType) errs.streetType = "Veuillez sélectionner un type de voie";
    const label = data.streetLabel.trim();
    if (!label) errs.streetLabel = "Libellé de voie obligatoire";
    else if (label.length > STREET_LABEL_MAX) errs.streetLabel = `Le libellé de voie ne doit pas dépasser ${STREET_LABEL_MAX} caractères`;
    else if (!STREET_TEXT_PATTERN.test(label)) errs.streetLabel = "Le libellé de voie contient des caractères non autorisés";
    if (data.complement.trim()) {
        if (data.complement.length > COMPLEMENT_MAX) errs.complement = `Le complément ne doit pas dépasser ${COMPLEMENT_MAX} caractères`;
        else if (!STREET_TEXT_PATTERN.test(data.complement.trim())) errs.complement = "Le complément contient des caractères non autorisés";
    }
    if (data.lieuDit.trim()) {
        if (data.lieuDit.length > COMPLEMENT_MAX) errs.lieuDit = `Le lieu-dit ne doit pas dépasser ${COMPLEMENT_MAX} caractères`;
        else if (!LIEU_DIT_PATTERN.test(data.lieuDit.trim())) errs.lieuDit = "Le lieu-dit contient des caractères non autorisés";
    }
    if (!validateZipCode(data.zipCode).isValid) errs.zipCode = validateZipCode(data.zipCode).message;
    if (!data.city.trim()) errs.city = "Veuillez sélectionner une ville";
    return errs;
};

const squash = (text: string): string => text.replace(/\s+/g, " ").trim();

/** « Bis Avenue Henri Martin » (lettre + type + libellé). */
export const composeStreetName = (data: AddressData): string =>
    squash(`${data.streetLetter?.name ?? ""} ${data.streetType?.name ?? ""} ${data.streetLabel}`);

/** « 6 Bis Avenue Henri Martin, 75016 Paris ». */
export const composeFormattedAddress = (data: AddressData): string =>
    squash(`${data.streetNumber} ${composeStreetName(data)}, ${data.zipCode} ${data.city}`);

/** Corps attendu par PUT /clients/v2/clients/{id}/addresses/manual. */
export interface ManualAddressPayload {
    streetNumber: string;
    streetLetter: string;
    streetType: string;
    streetLabel: string;
    complement: string;
    zip: string;
    city: string;
}

export const toManualAddressPayload = (data: AddressData): ManualAddressPayload => ({
    streetNumber: data.streetNumber.trim(),
    streetLetter: data.streetLetter?.name ?? "",
    streetType: data.streetType?.name ?? "",
    streetLabel: data.streetLabel.trim(),
    complement: data.complement.trim(),
    zip: data.zipCode.trim(),
    city: data.city.trim(),
});

/** Champ du serveur → champ du formulaire (pour afficher les erreurs renvoyées par l'API). */
export const apiErrorsToAddressErrors = (errors: unknown): AddressErrors => {
    const mapping: Record<string, keyof AddressErrors> = {
        streetNumber: "streetNumber",
        streetType: "streetType",
        streetLabel: "streetLabel",
        complement: "complement",
        zip: "zipCode",
        city: "city",
    };
    const result: AddressErrors = {};
    if (!Array.isArray(errors)) return result;
    errors.forEach((item: any) => {
        const key = mapping[item?.field];
        if (key && typeof item?.message === "string") result[key] = item.message;
    });
    return result;
};

const fold = (text: string): string =>
    text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\./g, "").trim();

export const findStreetLetter = (raw: string | undefined | null): VoieOption | null => {
    const needle = fold(raw ?? "");
    if (!needle) return null;
    return lettreVoieList.find((l) => fold(l.name) === needle) ?? null;
};

/**
 * Type de voie reconnu au début d'un libellé (nom complet ou abréviation),
 * le plus long d'abord (« Chemin vicinal » avant « Chemin »).
 */
export const findStreetType = (raw: string | undefined | null): { type: VoieOption; rest: string } | null => {
    const text = squash(raw ?? "");
    if (!text) return null;
    const folded = fold(text);
    const candidates = [...typeVoie].sort((a, b) => b.name.length - a.name.length);
    for (const candidate of candidates) {
        const name = fold(candidate.name);
        if (folded === name || folded.startsWith(name + " ")) {
            return { type: candidate, rest: text.slice(candidate.name.length).trim() };
        }
    }
    const firstWord = folded.split(" ")[0];
    const expanded = STREET_TYPE_ABBREVIATIONS[firstWord];
    if (expanded) {
        const type = typeVoie.find((t) => fold(t.name) === fold(expanded));
        if (type) {
            return { type, rest: text.split(/\s+/).slice(1).join(" ") };
        }
    }
    return null;
};

/**
 * Pré-remplit le formulaire depuis une adresse enregistrée, qu'elle vienne de
 * Google Places (« 6 » + « Avenue Henri Martin ») ou du formulaire AICI
 * (« 6 » + « Bis Avenue Henri Martin »). Ce qui n'est pas reconnu reste dans
 * le libellé : l'utilisateur corrige à la main.
 */
export const addressToForm = (address: Address | null | undefined): AddressData => {
    const form = emptyAddressData();
    if (!address) return form;

    const rawNumber = `${address.streetNumber ?? ""}`.trim();
    const numberMatch = rawNumber.match(/\d{1,4}/);
    form.streetNumber = numberMatch ? String(parseInt(numberMatch[0], 10)) : "";
    form.streetLetter = findStreetLetter(rawNumber.replace(/\d+/g, "").replace(/[-\s]/g, ""));

    let street = squash(`${address.streetName ?? ""}`);
    if (!form.streetLetter) {
        const [first, ...others] = street.split(" ");
        const letter = findStreetLetter(first);
        // une lettre seule (« A », « B ») n'est une lettre de voie que devant un type de voie
        if (letter && others.length > 0 && (first.length > 1 || findStreetType(others.join(" ")))) {
            form.streetLetter = letter;
            street = others.join(" ");
        }
    }

    const typed = findStreetType(street);
    if (typed) {
        form.streetType = typed.type;
        form.streetLabel = typed.rest.slice(0, STREET_LABEL_MAX);
    } else {
        form.streetLabel = street.slice(0, STREET_LABEL_MAX);
    }

    form.complement = (address.extraInformation ?? "").slice(0, COMPLEMENT_MAX);
    form.zipCode = `${address.zip ?? ""}`.replace(/\D/g, "").slice(0, 5);
    form.city = `${address.city ?? ""}`.trim();
    return form;
};
